import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import { ContentRequest, ContentRequestState } from '@amzn/awscat-pattern-library-service-model';
import React, { useState } from 'react';

import constants from '../../common/constants';
import RouterPaths from '../../common/RouterPaths';
import UserIdAutosuggest from '../common/UserIdAutosuggest';
import BrowserHistory from '../utils/BrowserHistory';

import '../../resources/styles/scss/components/content-request/content-request-assignment-table.scss';

interface Props {
  contentRequest: ContentRequest;
  updateContentRequest: (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId: string,
  ) => void;
}

const CreateContentActionButtons = ({ contentRequest, updateContentRequest }: Props) => {
  const [showAcceptModal, setShowAcceptModal] = useState<boolean>(false);
  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [isDeclining, setIsDeclining] = useState<boolean>(false);
  const [isCreatingLater, setIsCreatingLater] = useState<boolean>(false);
  const [isCreatingNow, setIsCreatingNow] = useState<boolean>(false);
  const [newAssigneeId, setNewAssigneeId] = useState<string>('');
  const [isCreatingDraft, setIsCreatingDraft] = useState<boolean>(false);

  const handleAcceptAssignment = () => setShowAcceptModal(true);
  const handleDeclineAssignment = () => setShowDeclineModal(true);
  const onAcceptModalDismissClick = () => setShowAcceptModal(false);
  const onDeclineModalDismissClick = () => setShowDeclineModal(false);

  const handleCreateLater = async () => {
    setIsCreatingLater(true);
    const { requestId, assigneeId } = contentRequest;
    await updateContentRequest(requestId, ContentRequestState.ACCEPTED, assigneeId ?? '');
    setIsCreatingLater(false);
    onAcceptModalDismissClick();
  };

  const handleCreateNow = async () => {
    setIsCreatingNow(true);
    const { requestId, assigneeId } = contentRequest;
    await updateContentRequest(requestId, ContentRequestState.ACCEPTED, assigneeId ?? '');
    setIsCreatingNow(false);
    onAcceptModalDismissClick();
    BrowserHistory.push(RouterPaths.CREATE_CONTENT, { relatedContentRequest: contentRequest });
  };

  const handleCreateDraft = async () => {
    const { requestId, assigneeId } = contentRequest;
    setIsCreatingDraft(true);
    await updateContentRequest(requestId, ContentRequestState.ACCEPTED, assigneeId ?? '');
    setIsCreatingDraft(false);
    BrowserHistory.push(RouterPaths.CREATE_CONTENT, { relatedContentRequest: contentRequest });
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    const { requestId } = contentRequest;
    const newState = newAssigneeId ? ContentRequestState.ASSIGNED : ContentRequestState.REQUESTED;
    await updateContentRequest(requestId, newState, newAssigneeId);
    setIsDeclining(false);
    onDeclineModalDismissClick();
  };

  const handleAssigneeSelected = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: any,
  ) => {
    if (!validationError) {
      setNewAssigneeId(selectedUserId);
    } else if (newAssigneeId) {
      setNewAssigneeId('');
    }
  };

  const acceptFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onAcceptModalDismissClick}>
          Cancel
        </Button>
        <Button variant="normal" onClick={handleCreateLater} loading={isCreatingLater}>
          {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_CREATE_LATER}
        </Button>
        <Button variant="primary" loading={isCreatingNow} onClick={handleCreateNow}>
          {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_CREATE_NOW}
        </Button>
      </SpaceBetween>
    </Box>
  );

  const declineFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onDeclineModalDismissClick}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleDecline} loading={isDeclining}>
          Decline
        </Button>
      </SpaceBetween>
    </Box>
  );

  const renderActionButtons = () => {
    const { state } = contentRequest;
    if (state === ContentRequestState.ACCEPTED) {
      return (
        <Button variant="normal" onClick={handleCreateDraft} loading={isCreatingDraft}>
          {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_CREATE_DRAFT}
        </Button>
      );
    }
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="normal" onClick={handleAcceptAssignment}>
          Accept
        </Button>
        <Button variant="normal" onClick={handleDeclineAssignment}>
          Decline
        </Button>
      </SpaceBetween>
    );
  };

  return (
    <>
      {renderActionButtons()}
      <Modal
        visible={showAcceptModal}
        header={constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_ACCEPT_HEADER}
        footer={acceptFooter}
        onDismiss={onAcceptModalDismissClick}
        closeAriaLabel="Close modal"
      >
        {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_ACCEPT_BODY}
      </Modal>
      <Modal
        visible={showDeclineModal}
        header={constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_DECLINE_HEADER}
        footer={declineFooter}
        onDismiss={onDeclineModalDismissClick}
        closeAriaLabel="Close modal"
      >
        <div>
          <p>
            {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_DECLINE_BODY}
            <i> - Optional</i>
          </p>
          <Box color="text-body-secondary">
            {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_DECLINE_BODY_DESC}
          </Box>
          <UserIdAutosuggest onUserSelected={handleAssigneeSelected} />
        </div>
      </Modal>
    </>
  );
};

export default CreateContentActionButtons;
